import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Building2,
  ClipboardCheck,
  Filter,
  Hammer,
  Hourglass,
  RefreshCw,
} from "lucide-react";
import { api } from "../../api";
import { hasAnyRole, useAuth } from "../../auth";
import { cx, PageHeader } from "../../ui";

/**
 * Mobile-first queue for PMs and Chief Engineers walking the
 * building. Lists only the projects in their managed buildings
 * that are touchable today (in_progress, inspection_required,
 * scheduled, conditionally_approved). Single column, large
 * tap targets, big status badges, no chrome — when you're on a
 * phone in a hallway you don't want a desktop nav fighting for
 * space.
 *
 * Filter chips keep the list short. The default ("Active today")
 * shows in_progress + inspection_required, since those are what
 * a PM/CE typically wants when on-site.
 */

type InspectorRow = {
  id: string;
  title: string;
  buildingId: string;
  unitNumber: string;
  buildingName: string;
  statusCode: string;
  updatedAt: string;
};

type FilterKey = "active" | "inspection" | "scheduled" | "all";

const FILTERS: Array<{
  key: FilterKey;
  label: string;
  match: (row: InspectorRow) => boolean;
}> = [
  {
    key: "active",
    label: "Active today",
    match: (r) =>
      r.statusCode === "in_progress" || r.statusCode === "inspection_required",
  },
  {
    key: "inspection",
    label: "Inspection due",
    match: (r) => r.statusCode === "inspection_required",
  },
  {
    key: "scheduled",
    label: "Scheduled",
    match: (r) => r.statusCode === "scheduled",
  },
  {
    key: "all",
    label: "All",
    match: () => true,
  },
];

const STATUS_LABEL: Record<string, string> = {
  in_progress: "In progress",
  inspection_required: "Inspection due",
  scheduled: "Scheduled",
  conditionally_approved: "Conditionally approved",
};

const STATUS_TONE: Record<string, string> = {
  in_progress: "bg-status-approved-bg text-status-approved-fg",
  inspection_required: "bg-status-rejected-bg text-status-rejected-fg",
  scheduled: "bg-status-submitted-bg text-status-submitted-fg",
  conditionally_approved: "bg-status-review-bg text-status-review-fg",
};

export function InspectQueue() {
  const { state } = useAuth();
  const [rows, setRows] = useState<InspectorRow[] | null>(null);
  const [filter, setFilter] = useState<FilterKey>("active");
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reload = async (showSpinner = true) => {
    if (showSpinner) setRefreshing(true);
    try {
      const r = await api.get<{ data: InspectorRow[] }>(
        "/api/management/inspector/queue",
      );
      setRows(r.data);
      setError(null);
    } catch (err) {
      setError(String(err));
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => {
    void reload(false);
  }, []);

  const filtered = useMemo(() => {
    if (!rows) return [];
    const matcher = FILTERS.find((f) => f.key === filter)!.match;
    return rows.filter(matcher);
  }, [rows, filter]);

  const counts = useMemo(() => {
    const c: Record<FilterKey, number> = {
      active: 0,
      inspection: 0,
      scheduled: 0,
      all: 0,
    };
    if (!rows) return c;
    for (const r of rows) {
      for (const f of FILTERS) {
        if (f.match(r)) c[f.key]++;
      }
    }
    return c;
  }, [rows]);

  if (state.status !== "authenticated") return null;
  const allowed = hasAnyRole(state.me, [
    "property_manager",
    "admin",
    "chief_engineer",
  ]);
  if (!allowed) {
    return (
      <div className="px-4 py-8 max-w-prose">
        <h1 className="text-ink-800 text-xl m-0 mb-2">Inspector view</h1>
        <p className="text-ink-600 text-[14px]">
          The inspector view is for Property Management, Site Admin, and Chief
          Engineer roles. If you should have access, contact your building's
          admin.
        </p>
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title={
          <span className="inline-flex items-center gap-2.5">
            <ClipboardCheck size={24} className="opacity-80" />
            Inspector
          </span>
        }
        subtitle="Mobile workflow for walking the building. Open a project to take photos, drop a note, or mark inspections complete."
        actions={
          <button
            type="button"
            onClick={() => void reload()}
            className={cx(
              "inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[12px] text-white/80 hover:text-white hover:bg-white/[0.09]",
              refreshing && "opacity-60",
            )}
            disabled={refreshing}
          >
            <RefreshCw
              size={13}
              className={refreshing ? "animate-spin" : undefined}
            />
            Refresh
          </button>
        }
      />

      {error && (
        <div className="px-4 py-2 -mt-2 mb-2">
          <div className="alert-error">{error}</div>
        </div>
      )}

      <div className="-mx-4 sm:-mx-6 lg:-mx-8 px-3 py-3 bg-sand-50 border-y border-sand-100 sticky top-[58px] z-[9]">
        <div className="flex items-center gap-1.5 overflow-x-auto no-scrollbar -mx-1 px-1 snap-x snap-mandatory">
          <Filter size={13} className="text-ink-400 flex-shrink-0" />
          {FILTERS.map((f) => (
            <button
              key={f.key}
              type="button"
              onClick={() => setFilter(f.key)}
              className={cx(
                "inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-[12px] whitespace-nowrap border snap-start shrink-0",
                filter === f.key
                  ? "bg-brand-700 text-white border-brand-700"
                  : "bg-white text-ink-700 border-sand-200 hover:bg-sand-100",
              )}
            >
              {f.label}
              <span
                className={cx(
                  "inline-flex items-center justify-center min-w-[18px] h-[16px] px-1.5 rounded-full text-[10px] font-semibold",
                  filter === f.key
                    ? "bg-white/20 text-white"
                    : "bg-sand-100 text-ink-600",
                )}
              >
                {counts[f.key]}
              </span>
            </button>
          ))}
        </div>
      </div>

      {rows === null ? (
        <p className="text-ink-500 text-sm px-4 py-6 m-0">Loading…</p>
      ) : filtered.length === 0 ? (
        <p className="text-ink-500 text-sm px-4 py-6 m-0">
          {rows.length === 0
            ? "No active projects in your buildings right now."
            : "Nothing matches that filter."}
        </p>
      ) : (
        <ul className="m-0 p-0 list-none divide-y divide-sand-100">
          {filtered.map((r) => (
            <li key={r.id}>
              <Link
                to={`/inspect/${r.id}`}
                className="flex items-center gap-3 px-4 py-3.5 active:bg-sand-100 no-underline hover:no-underline"
              >
                <div className="flex-1 min-w-0">
                  <div className="text-[14.5px] font-semibold text-ink-800 truncate">
                    {r.title}
                  </div>
                  <div className="text-[12px] text-ink-500 mt-0.5 inline-flex items-center gap-1.5">
                    <Building2 size={11} />
                    {r.buildingName} · Unit {r.unitNumber}
                  </div>
                  <div className="mt-2 inline-flex items-center gap-2 flex-wrap">
                    <span
                      className={cx(
                        "inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10.5px] font-semibold",
                        STATUS_TONE[r.statusCode] ??
                          "bg-sand-100 text-ink-600",
                      )}
                    >
                      {r.statusCode === "inspection_required" ? (
                        <Hourglass size={10} />
                      ) : (
                        <Hammer size={10} />
                      )}
                      {STATUS_LABEL[r.statusCode] ?? r.statusCode}
                    </span>
                    <span className="text-[11px] text-ink-400">
                      {formatRelative(r.updatedAt)}
                    </span>
                  </div>
                </div>
                <ArrowRight
                  size={18}
                  className="text-ink-400 flex-shrink-0"
                />
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function formatRelative(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  const m = Math.floor(ms / 60_000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}
